export const shouldShowCreationTypeChoice = ({ userId, creationTypeConfirmed }) =>
	!userId && creationTypeConfirmed !== true;

export const getCreationSectionOrder = userType => ({
	identity: 2,
	aiAgent: userType === "ai_agent" ? 3 : 5,
	attendance: userType === "ai_agent" ? 4 : 3,
	accessSchedule: userType === "ai_agent" ? 5 : 4,
});

export const shouldShowAiAgentWizard = ({ userType, setupMode }) =>
	userType === "ai_agent" && setupMode !== "advanced";

const parseMetadata = value => {
	let parsed = value;
	for (let attempt = 0; attempt < 2 && typeof parsed === "string"; attempt += 1) {
		try {
			parsed = JSON.parse(parsed);
		} catch (_err) {
			return {};
		}
	}
	return parsed;
};

const isRecord = value =>
	Boolean(value) && typeof value === "object" && !Array.isArray(value);

const withoutExpandedStringKeys = value =>
	Object.fromEntries(
		Object.entries(value).filter(([key]) =>
			!/^\d+$/.test(key) && !["__proto__", "prototype", "constructor"].includes(key)
		)
	);

export const normalizeAiAgentMetadata = value => {
	const parsed = parseMetadata(value);
	if (!isRecord(parsed)) return {};

	const normalized = withoutExpandedStringKeys(parsed);
	const businessData = parseMetadata(normalized.businessData);
	if (isRecord(businessData)) {
		normalized.businessData = withoutExpandedStringKeys(businessData);
	} else if (normalized.businessData !== undefined) {
		normalized.businessData = {};
	}

	return normalized;
};

const hasText = (value, minimumLength = 1) =>
	String(value || "").trim().length >= minimumLength;

const hasValidEmail = value =>
	/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());

export const getHumanCreationReadiness = ({ name, email, password }) => {
	const checks = {
		name: hasText(name, 2),
		email: hasValidEmail(email),
		password: hasText(password, 5),
	};

	return {
		checks,
		isReady: Object.values(checks).every(Boolean),
		stepReady: [checks.name && checks.email && checks.password, true, true],
	};
};

export const getAiCreationReadiness = ({
	name,
	templateKey,
	selectedCapabilityKeys,
	businessData,
	whatsappId,
	selectedQueueIds,
	autonomyLevel,
}) => {
	const capabilityKeys = Array.isArray(selectedCapabilityKeys)
		? selectedCapabilityKeys
		: [];
	const requiresScheduling = capabilityKeys.some(key =>
		["scheduling", "appointment_management", "demo_scheduling"].includes(key)
	);
	const requiresAdvancedOperations = capabilityKeys.some(key =>
		["advanced_operations", "advanced_integrations"].includes(key)
	);
	const schedulingUrl = String(businessData?.schedulingUrl || "").trim();
	const hasValidSchedulingUrl = /^https?:\/\/[^\s]+$/i.test(schedulingUrl);
	const checks = {
		name: hasText(name, 2),
		template: hasText(templateKey),
		organization: hasText(businessData?.organizationName),
		capabilities: capabilityKeys.length > 0,
		connection: Boolean(whatsappId),
		queues: Array.isArray(selectedQueueIds) && selectedQueueIds.length > 0,
		scheduling: !requiresScheduling || hasValidSchedulingUrl,
		operations:
			!requiresAdvancedOperations || hasText(businessData?.advancedOperations, 5),
		autonomy:
			autonomyLevel !== "automatic" ||
			!requiresAdvancedOperations ||
			hasText(businessData?.advancedOperations, 5),
	};

	return {
		checks,
		isReady: Object.values(checks).every(Boolean),
		stepReady: [
			checks.name && checks.template,
			checks.organization,
			checks.capabilities &&
				checks.connection &&
				checks.queues &&
				checks.scheduling &&
				checks.operations &&
				checks.autonomy,
			true,
			true,
		],
	};
};

const intelligenceUsage = {
	essential: { maxOutputTokens: 350, relativeCost: "low" },
	balanced: { maxOutputTokens: 600, relativeCost: "medium" },
	advanced: { maxOutputTokens: 900, relativeCost: "high" },
};

export const getAiUsageEstimate = intelligenceProfile =>
	intelligenceUsage[intelligenceProfile] || intelligenceUsage.balanced;

const advancedProfileFields = [
	"description",
	"instructions",
	"llmProvider",
	"llmModel",
	"temperature",
	"maxTokens",
	"capabilities",
];

export const captureAiAdvancedModeDraft = profile => {
	const currentProfile = profile && typeof profile === "object" ? profile : {};
	return Object.fromEntries(
		advancedProfileFields.map(field => [field, currentProfile[field]])
	);
};

export const switchAiAgentSetupMode = (profile, targetMode, advancedDraft = null) => {
	const currentProfile = profile && typeof profile === "object" ? profile : {};
	const metadata = normalizeAiAgentMetadata(currentProfile.metadata);
	const currentMode = metadata.setupMode === "advanced" ? "advanced" : "guided";
	if (targetMode === currentMode) return currentProfile;

	if (targetMode === "guided") {
		return {
			...currentProfile,
			metadata: { ...metadata, setupMode: "guided" },
		};
	}

	return {
		...currentProfile,
		...(isRecord(advancedDraft) ? advancedDraft : {}),
		metadata: { ...metadata, setupMode: "advanced" },
	};
};

export const shouldUseSimplifiedCreationWizard = ({
	userId,
	creationTypeConfirmed,
	userType,
	setupMode,
}) =>
	creationTypeConfirmed === true &&
	((!userId && userType === "human") ||
		(userType === "ai_agent" && setupMode !== "advanced"));
